import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, FieldTextInput, PrimaryButton } from '../../../components';

import css from './ContactUsForm.module.css';

const FORMSPREE_FORM_ID = process.env.REACT_APP_FORMSPREE_FORM_ID;

/**
 * The values of the contact form, ready to be sent.
 *
 * @param {Object} values - The form values
 * @returns {{ name: string, email: string, message: string }}
 */
export const contactUsPayload = values => ({
  name: values.name?.trim() || '',
  email: values.email?.trim() || '',
  message: values.message?.trim() || '',
});

// Sends the message with Formspree: https://help.formspree.io/hc/en-us/articles/360013470814
const sendContactMessage = payload => {
  if (!FORMSPREE_FORM_ID) {
    return Promise.reject(new Error('REACT_APP_FORMSPREE_FORM_ID is not set'));
  }
  return fetch(`https://formspree.io/f/${FORMSPREE_FORM_ID}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...payload, _replyto: payload.email }),
  }).then(res =>
    res.ok
      ? res.json()
      : res.json().then(
          data => Promise.reject(new Error(data?.error || `Formspree error ${res.status}`)),
          () => Promise.reject(new Error(`Formspree error ${res.status}`))
        )
  );
};

/**
 * Contact form of the contact us page.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @returns {JSX.Element}
 */
const ContactUsForm = props => {
  const { className } = props;
  const intl = useIntl();
  const [status, setStatus] = useState(null); // null | 'success' | 'error'

  const onSubmit = (values, form) => {
    setStatus(null);
    return sendContactMessage(contactUsPayload(values))
      .then(() => {
        setStatus('success');
        // Next tick: Final Form doesn't allow resetting while the submit is still in progress.
        // restart() also clears the touched state, so the empty fields don't show errors.
        setTimeout(() => form.restart());
      })
      .catch(e => {
        console.error(e);
        setStatus('error');
      });
  };

  const nameRequired = validators.required(
    intl.formatMessage({ id: 'ContactUsForm.nameRequired' })
  );
  const emailValid = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'ContactUsForm.emailRequired' })),
    validators.emailFormatValid(intl.formatMessage({ id: 'ContactUsForm.emailInvalid' }))
  );
  const messageRequired = validators.required(
    intl.formatMessage({ id: 'ContactUsForm.messageRequired' })
  );

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={formRenderProps => {
        const { handleSubmit, submitting, invalid } = formRenderProps;
        return (
          <Form className={classNames(css.root, className)} onSubmit={handleSubmit}>
            <FieldTextInput
              id="contactUs.name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder={intl.formatMessage({ id: 'ContactUsForm.namePlaceholder' })}
              validate={nameRequired}
            />
            <FieldTextInput
              id="contactUs.email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={intl.formatMessage({ id: 'ContactUsForm.emailPlaceholder' })}
              validate={emailValid}
            />
            <FieldTextInput
              id="contactUs.message"
              name="message"
              type="textarea"
              className={css.fullWidth}
              inputRootClass={css.message}
              placeholder={intl.formatMessage({ id: 'ContactUsForm.messagePlaceholder' })}
              validate={messageRequired}
            />

            {status === 'success' ? (
              <p className={classNames(css.success, css.fullWidth)}>
                <FormattedMessage id="ContactUsForm.success" />
              </p>
            ) : status === 'error' ? (
              <p className={classNames(css.error, css.fullWidth)}>
                <FormattedMessage id="ContactUsForm.error" />
              </p>
            ) : null}

            <PrimaryButton
              className={classNames(css.submitButton, css.fullWidth)}
              type="submit"
              inProgress={submitting}
              disabled={submitting || invalid}
            >
              <FormattedMessage id="ContactUsForm.submit" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

export default ContactUsForm;
